import { useState, useEffect } from "react";

/**
 * Custom hook to track countdown to Watch market opening time (13:30 UTC daily, 15 mins only).
 * Opens at 13:30 UTC and closes at 13:45 UTC. Matches cron: 30 13 * * * (13:30 UTC).
 * @returns {Object} timeRemaining and isMarketOpen state
 */
const useWatchMarketCountdown = () => {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMarketOpen, setIsMarketOpen] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();

      // Get UTC time directly (13:30 UTC)
      const utcHours = now.getUTCHours();
      const utcMinutes = now.getUTCMinutes();

      // Target time: 13:30 UTC (matches cron 30 13 * * *)
      const targetHour = 13;
      const targetMinute = 30;

      // Market duration: 15 minutes (13:30 - 13:45 UTC)
      const marketDurationMinutes = 15;

      // Calculate if market is currently open
      const marketIsOpen = utcHours === targetHour &&
        utcMinutes >= targetMinute &&
        utcMinutes < targetMinute + marketDurationMinutes;
      setIsMarketOpen(marketIsOpen);

      // Calculate time remaining
      let hoursRemaining, minutesRemaining, secondsRemaining;

      if (marketIsOpen) {
        // Market is open - calculate time until close
        const closeMinute = targetMinute + marketDurationMinutes;
        minutesRemaining = closeMinute - utcMinutes - 1;
        hoursRemaining = 0;
        secondsRemaining = 60 - now.getSeconds();
        if (secondsRemaining === 60) secondsRemaining = 0;
      } else {
        // Market is closed - calculate time until next market open
        if (utcHours < targetHour ||
            (utcHours === targetHour && utcMinutes < targetMinute)) {
          // Market opens later today
          hoursRemaining = targetHour - utcHours;
          minutesRemaining = targetMinute - utcMinutes;
        } else {
          // Market opens tomorrow
          hoursRemaining = 24 - utcHours + targetHour;
          minutesRemaining = targetMinute - utcMinutes;
        }

        // Adjust for negative minutes
        if (minutesRemaining < 0) {
          hoursRemaining--;
          minutesRemaining += 60;
        }

        secondsRemaining = 60 - now.getSeconds();
        if (secondsRemaining === 60) secondsRemaining = 0;

        // If seconds is 0, we don't need to adjust minutes
        if (secondsRemaining > 0 && secondsRemaining < 60) {
          if (minutesRemaining === 0) {
            hoursRemaining--;
            minutesRemaining = 59;
          } else {
            minutesRemaining--;
          }
        }
      }

      setTimeRemaining({
        hours: hoursRemaining,
        minutes: minutesRemaining,
        seconds: secondsRemaining
      });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, []);

  return { timeRemaining, isMarketOpen };
};

export default useWatchMarketCountdown;
